import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type PayoutStatus = 'awaiting_confirmation' | 'requested' | 'pending' | 'completed' | 'cancelled';
export type PayoutTargetType = 'web3' | 'fiat' | 'special';

export interface Payout {
  id: number;
  sender_id: number;
  sender_email?: string;
  sender_username?: string;
  sender_full_name?: string;
  sender_first_name?: string;
  sender_last_name?: string;
  account_id: number;
  account_username?: string;
  status: PayoutStatus;
  target_type: PayoutTargetType;
  web3_token?: 'USDT' | 'USDC';
  amount: number;
  fee?: number;
  amount_sent?: number;
  net_amount: number;
  subject?: string;
  description?: string;
  requested_at?: string;
  start_pending_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  commissions_calculated?: boolean;
  accounted?: boolean;
  // Web3 fields
  web3_target_wallet?: string;
  web3_from_address?: string;
  web3_tx_hash?: string;
  web3_confirmations?: number;
  web3_block_number?: number;
  web3_verified_at?: string;
  web3_last_check_at?: string;
  // Fiat fields
  fiat_target_wallet?: string;
  fiat_target_name?: string;
  fiat_verified_at?: string;
  fiat_last_check_at?: string;
  // Super admin approval
  super_admin_approved?: boolean;
  super_admin_approved_at?: string;
}

export interface PayoutFilters {
  filter_status?: PayoutStatus;
  filter_target_type?: PayoutTargetType;
  filter_sender_id?: number;
  filter_account_id?: number;
  filter_created_at_start?: string;
  filter_created_at_end?: string;
  page?: number;
  per_page?: number;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface PayoutListResponse {
  payouts: Payout[];
  total_count: number;
  total_amount: number;
  total_fee: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface PayoutAccount {
  id: number;
  username: string;
  email: string;
}

export interface PayoutSender {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  balance: number;
  user_type?: string;
}

export interface PoolSwapQuote {
  available: boolean;
  from_token?: string;
  to_token?: string;
  amount_in?: number;
  expected_out?: number;
  expected_cost?: number;
  expected_cost_pct?: number;
  estimated_gas_bnb?: number | null;
  gas_price_gwei?: number | null;
  message?: string;
}

export interface PoolSwapStatus {
  id: number;
  status: 'pending' | 'completed' | 'failed';
  from_token: string;
  to_token: string;
  amount_in?: number | null;
  amount_out?: number | null;
  cost?: number | null;
  tx_hash?: string | null;
  error?: string | null;
  created_at?: string;
  message?: string;
  pool_balances?: { [currency: string]: number };
}

export interface PayoutAccountBalance {
  id: number;
  username: string;
  balance: number;
  usdt_bsc_balance?: number;
  usdc_bsc_balance?: number;
  usdt_bsc_wallet?: string;
  bsc_wallet?: string;
  token_balances?: { [token: string]: number };
  // Internal (ledger) balances per currency, e.g. { USDT: 1000, USDC: 0 }.
  currency_balances?: { [currency: string]: number };
}

export interface ProcessedPayout {
  payout_id: number;
  amount: number;
  target_type: string;
  tx_hash?: string;
}

export interface FailedPayout {
  payout_id: number;
  error: string;
  critical?: boolean;
}

export interface BatchProcessSummary {
  total_processed: number;
  total_failed: number;
  total_amount_processed: number;
}

export interface BatchProcessResponse {
  success: boolean;
  message?: string;
  error?: string;
  errors?: string[];
  processed: ProcessedPayout[];
  failed: FailedPayout[];
  summary: BatchProcessSummary;
}

export interface BatchValidationError {
  payout_id?: number;
  account_id?: number;
  errors: string[];
}

export interface BatchValidationWarning {
  payout_id?: number;
  account_id?: number;
  warnings: string[];
}

export interface BatchValidationSummary {
  total_payouts: number;
  total_amount: number;
  total_fee: number;
  accounts_involved: number[];
}

export interface BatchValidationResponse {
  valid: boolean;
  errors: BatchValidationError[];
  warnings: BatchValidationWarning[];
  summary: BatchValidationSummary;
}

export interface CreatePayoutRequest {
  sender_id: number;
  account_id: number;
  target_type: PayoutTargetType;
  amount: number;
  fee?: number;
  amount_sent?: number;
  subject?: string;
  description?: string;
  // Web3 fields
  web3_token?: 'USDT' | 'USDC';
  web3_target_wallet?: string;
  web3_from_address?: string;
  // Fiat fields
  fiat_target_wallet?: string;
  fiat_target_name?: string;
}

export interface UpdatePayoutRequest {
  status?: PayoutStatus;
  amount?: number;
  fee?: number;
  amount_sent?: number;
  subject?: string;
  description?: string;
  // Web3 fields
  web3_target_wallet?: string;
  web3_from_address?: string;
  web3_tx_hash?: string;
  web3_confirmations?: number;
  web3_block_number?: number;
  web3_verified_at?: string;
  // Fiat fields
  fiat_target_wallet?: string;
  fiat_target_name?: string;
  fiat_verified_at?: string;
  // Flags
  commissions_calculated?: boolean;
  accounted?: boolean;
  start_pending_at?: string;
  completed_at?: string;
}

@Injectable({ providedIn: 'root' })
export class PayoutService {
  constructor(private http: HttpClient) {}

  getPayouts(filters: PayoutFilters = {}): Observable<PayoutListResponse> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<PayoutListResponse>('/admin/payouts', { params });
  }

  getPayout(id: number): Observable<{ payout: Payout }> {
    return this.http.get<{ payout: Payout }>(`/admin/payouts/${id}`);
  }

  createPayout(data: CreatePayoutRequest): Observable<{
    payout: Payout;
    message: string;
    status?: string;
    confirmation_code_sent?: boolean;
    expires_at?: string;
  }> {
    return this.http.post<{
      payout: Payout;
      message: string;
      status?: string;
      confirmation_code_sent?: boolean;
      expires_at?: string;
    }>('/admin/payouts', data);
  }

  confirmPayout(id: number, confirmationCode: string): Observable<{ payout: Payout; message: string }> {
    return this.http.post<{ payout: Payout; message: string }>(`/admin/payouts/${id}/confirm`, {
      confirmation_code: confirmationCode
    });
  }

  resendConfirmationCode(id: number): Observable<{
    confirmation_code_sent: boolean;
    expires_at: string;
    message: string;
  }> {
    return this.http.post<{
      confirmation_code_sent: boolean;
      expires_at: string;
      message: string;
    }>(`/admin/payouts/${id}/resend_code`, {});
  }

  updatePayout(id: number, data: UpdatePayoutRequest): Observable<{ payout: Payout; message: string }> {
    return this.http.put<{ payout: Payout; message: string }>(`/admin/payouts/${id}`, data);
  }

  cancelPayout(id: number): Observable<{ payout: Payout; message: string }> {
    return this.http.post<{ payout: Payout; message: string }>(`/admin/payouts/${id}/cancel`, {});
  }

  getAccounts(): Observable<{ accounts: PayoutAccount[] }> {
    return this.http.get<{ accounts: PayoutAccount[] }>('/admin/payouts/accounts');
  }

  searchSenders(query: string): Observable<{ senders: PayoutSender[] }> {
    return this.http.get<{ senders: PayoutSender[] }>('/admin/payouts/senders', {
      params: { q: query }
    });
  }

  getAccountBalances(): Observable<{ accounts: PayoutAccountBalance[] }> {
    return this.http.get<{ accounts: PayoutAccountBalance[] }>('/admin/payouts/account_balances');
  }

  // Admin: read-only expected output for a pool swap (QuoterV2, no gas).
  swapQuote(fromToken: 'USDT' | 'USDC', toToken: 'USDT' | 'USDC', amount: number): Observable<PoolSwapQuote> {
    return this.http.get<PoolSwapQuote>('/admin/payouts/swap_quote', {
      params: { from_token: fromToken, to_token: toToken, amount: amount.toString() }
    });
  }

  // Admin: broadcast a USDT<->USDC pool swap. Returns a pending swap; poll
  // getSwapStatus() for confirmation + booking. Needs the pool wallet key.
  swapPool(params: {
    from_token: 'USDT' | 'USDC';
    to_token: 'USDT' | 'USDC';
    amount: number;
    wallet_key: string;
    slippage_bps?: number;
  }): Observable<PoolSwapStatus> {
    return this.http.post<PoolSwapStatus>('/admin/payouts/swap_pool', params);
  }

  // Poll a pool swap's status (pending → completed/failed).
  getSwapStatus(id: number): Observable<PoolSwapStatus> {
    return this.http.get<PoolSwapStatus>(`/admin/payouts/swaps/${id}`);
  }

  // Book an already-executed swap by its tx hash (recovers a swap whose receipt
  // was slow or that predates the async flow).
  reconcileSwap(params: {
    tx_hash: string;
    from_token: 'USDT' | 'USDC';
    to_token: 'USDT' | 'USDC';
  }): Observable<PoolSwapStatus> {
    return this.http.post<PoolSwapStatus>('/admin/payouts/swaps/reconcile', params);
  }

  batchProcess(payoutIds: number[], walletKey?: string): Observable<BatchProcessResponse> {
    return this.http.post<BatchProcessResponse>('/admin/payouts/batch_process', {
      payout_ids: payoutIds,
      wallet_key: walletKey
    });
  }

  validateBatch(payoutIds: number[], walletKey?: string): Observable<BatchValidationResponse> {
    return this.http.post<BatchValidationResponse>('/admin/payouts/validate_batch', {
      payout_ids: payoutIds,
      wallet_key: walletKey
    });
  }

  requestSuperAdminApproval(id: number): Observable<{
    approval_code_sent: boolean;
    expires_at: string;
    message: string;
  }> {
    return this.http.post<{
      approval_code_sent: boolean;
      expires_at: string;
      message: string;
    }>(`/admin/payouts/${id}/request_approval`, {});
  }

  superAdminApprove(id: number, approvalCode: string): Observable<{ payout: Payout; message: string }> {
    return this.http.post<{ payout: Payout; message: string }>(`/admin/payouts/${id}/super_approve`, {
      approval_code: approvalCode
    });
  }
}
